import { API_CALL_FAILURE, API_CALL_REQUEST, API_CALL_SUCCESS, CLEAR_LUX_PLUS_MEMBER_SUBSCRIPTION, SET_SHOW_LUX_PLUS_FREE_PREVIEW_MODAL } from 'actions/actionConstants'
import {
  FETCH_LUX_PLUS_MEMBER_FREE_PREVIEW_ELIGIBILITY,
  FETCH_LUX_PLUS_PRODUCT_SUBSCRIPTION,
  FETCH_LUX_PLUS_ORDER_REDEEMED_SAVINGS,
  FETCH_LUX_PLUS_ORDER_POTENTIAL_SAVINGS,
  FETCH_LUX_PLUS_MEMBERSHIP_METRICS,
  FETCH_LUX_PLUS_TOTAL_POTENTIAL_SAVINGS,
  FETCH_LUX_PLUS_ACCUMULATIVE_SAVINGS,
} from 'actions/apiActionConstants'
import { createAppReducer, apiReducerSwitch } from 'lib/redux/reducerUtils'
import { LUXURY_PLUS } from 'luxPlus/constants/base'

export const initialLuxPlusState: App.LuxPlusState = {
  products: {
    subscription: {
      offers: undefined,
      fetching: false,
      error: undefined,
    },
    freePreview: {
      offer: undefined,
      fetching: false,
      error: undefined,
    },
  },
  orderSavings: {},
  membershipMetrics: { initial: true },
  totalPotentialSavings: { initial: true },
  accumulativeSavings: { initial: true },
}

const apiCalls = apiReducerSwitch<App.LuxPlusState>({
  [FETCH_LUX_PLUS_PRODUCT_SUBSCRIPTION]: {
    request: (state) => ({
      products: {
        ...state.products,
        subscription: {
          fetching: true,
          error: undefined,
        },
      },
    }),
    success: (state, action) => ({
      products: {
        ...state.products,
        subscription: {
          offers: action.data,
          fetching: false,
          error: undefined,
        },
      },
    }),
    failure: (state, action) => ({
      products: {
        ...state.products,
        subscription: {
          fetching: false,
          error: action.error,
        },
      },
    }),
  },
  [FETCH_LUX_PLUS_MEMBER_FREE_PREVIEW_ELIGIBILITY]: {
    request: (state) => ({
      products: {
        ...state.products,
        freePreview: {
          fetching: true,
          error: undefined,
        },
      },
    }),
    success: (state, action) => ({
      products: {
        ...state.products,
        freePreview: {
          offer: action.data,
          fetching: false,
          error: undefined,
        },
      },
    }),
    failure: (state, action) => ({
      products: {
        ...state.products,
        freePreview: {
          fetching: false,
          error: action.error,
        },
      },
    }),
  },
  [FETCH_LUX_PLUS_ORDER_REDEEMED_SAVINGS]: {
    request: (state, action) => ({
      orderSavings: {
        ...state.orderSavings,
        [action.orderId]: {
          fetching: true,
          error: undefined,
        },
      },
    }),
    success: (state, action) => ({
      orderSavings: {
        ...state.orderSavings,
        [action.orderId]: {
          fetching: false,
          error: undefined,
          data: {
            totalSavings: action.data.totalSavings,
            currency: action.data.currency ?? '',
            wasMemberAtBooking: true,
            shouldDisplay: action.data.totalSavings > 0,
          },
        },
      },
    }),
    failure: (state, action) => ({
      orderSavings: {
        ...state.orderSavings,
        [action.orderId]: {
          fetching: false,
          error: action.error,
        },
      },
    }),
  },
  [FETCH_LUX_PLUS_ORDER_POTENTIAL_SAVINGS]: {
    request: (state, action) => ({
      orderSavings: {
        ...state.orderSavings,
        [action.orderId]: {
          fetching: true,
          error: undefined,
        },
      },
    }),
    success: (state, action) => {
      const potentialSavings = action.data.totalDisplayCurrencyAmount || 0
      return {
        orderSavings: {
          ...state.orderSavings,
          [action.orderId]: {
            fetching: false,
            error: undefined,
            data: {
              totalSavings: potentialSavings,
              currency: action.data.displayCurrency,
              wasMemberAtBooking: false,
              shouldDisplay: potentialSavings >= LUXURY_PLUS.NON_MEMBER_SAVINGS_DISPLAY_THRESHOLD,
              items: (action.data.items ?? []).map(item => ({
                itemId: item.itemId,
                displayAmount: item.displayAmount,
              })),
            },
          },
        },
      }
    },
    failure: (state, action) => ({
      orderSavings: {
        ...state.orderSavings,
        [action.orderId]: {
          fetching: false,
          error: action.error,
          data: {
            totalSavings: 0,
            currency: '',
            wasMemberAtBooking: false,
            shouldDisplay: false,
          },
        },
      },
    }),
  },
  [FETCH_LUX_PLUS_MEMBERSHIP_METRICS]: {
    request: () => ({
      membershipMetrics: { fetching: true },
    }),
    success: (_state, action) => ({
      membershipMetrics: { fetching: false, data: action.data },
    }),
    failure: (_state, action) => ({
      membershipMetrics: { fetching: false, error: action.error },
    }),
  },
  [FETCH_LUX_PLUS_TOTAL_POTENTIAL_SAVINGS]: {
    request: () => ({
      totalPotentialSavings: { fetching: true },
    }),
    success: (_state, action) => ({
      totalPotentialSavings: { fetching: false, data: action.data },
    }),
    failure: (_state, action) => ({
      totalPotentialSavings: { fetching: false, error: action.error },
    }),
  },
  [FETCH_LUX_PLUS_ACCUMULATIVE_SAVINGS]: {
    request: () => ({
      accumulativeSavings: { fetching: true },
    }),
    success: (_state, action) => ({
      accumulativeSavings: {
        fetching: false,
        data: {
          totalSavings: action.data.totalDisplayCurrencyAmount ?? 0,
          currency: action.data.displayCurrency,
          // Keep the per-order items so the checkout upsell can re-derive a flight-gated total
          // (the server total bundles flights region-agnostically). See usePotentialSavingsRows.
          items: action.data.items ?? [],
        },
      },
    }),
    failure: (_state, action) => ({
      accumulativeSavings: { fetching: false, error: action.error },
    }),
  },
})

export default createAppReducer<App.LuxPlusState>(initialLuxPlusState, {
  [API_CALL_REQUEST]: (state, action) => apiCalls(state, action),
  [API_CALL_FAILURE]: (state, action) => apiCalls(state, action),
  [API_CALL_SUCCESS]: (state, action) => apiCalls(state, action),
  [CLEAR_LUX_PLUS_MEMBER_SUBSCRIPTION]: (state, _action) => ({
    products: {
      ...state.products,
      freePreview: {
        ...initialLuxPlusState.products.freePreview,
      },
    },
  }),
  [SET_SHOW_LUX_PLUS_FREE_PREVIEW_MODAL]: (_state, action) => ({
    showFreePreviewEnrolledModal: action.show,
    freePreviewEnrolledTrigger: action.trigger,
  }),
})
